<template>
    <Notification
        variant="positive"
        :title="isCheckout ? undefined : title"
        :tier="2"
        padding="l"
        :icon="isBooking ? undefined : 'Check'">
        <div class="notification-content">
            <div>
                <span v-if="isCheckout" class="notification-content-title">
                    {{ title }}
                </span>
                <span
                    class="notification-content-description"
                    v-html="sanitize(cancellationDescription)" />
            </div>

            <ButtonLink size="m" :onClick="() => onClick(productId)">
                {{ t({ key: 'cancellation.more_about_cancellation' }) }}
            </ButtonLink>
        </div>
    </Notification>
</template>

<script lang="ts">
    import { computed, ComputedRef, defineComponent, PropType } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { useStore } from '@/store';
    import { AnyProduct, MainProduct } from '@shared/interfaces/product';
    import { ProductType } from '@/api/Cart/cart.interfaces';
    import { isMainProduct } from '@/api/Cart/cart.helpers';
    import { convertToDate } from '@shared/helpers/datetime/datetime';
    import {
        getCartProductCancellation,
        getFirstAmountCartPolicy,
    } from '@/components/shared/Cancellation/CartCancellation/CartCancellation.helpers';
    import { CartProductCancellation } from '@/components/shared/Cancellation/CartCancellation/CartCancellation.interfaces';
    import { getLocaleDayShortMonth } from '@shared/helpers/datetime/datetime.formatters';
    import { sanitize } from '@shared/utils/sanitize';
    import Notification from '@ui/components/Notification/Notification.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';

    export default defineComponent({
        name: 'RefundableNotification',
        methods: { t, sanitize },
        components: {
            Notification,
            ButtonLink,
        },
        props: {
            onClick: { type: Function, required: true },
            productId: { type: String },
            placement: {
                type: String as PropType<'checkout' | 'payment' | 'booking'>,
                required: true,
            },
            date: { type: Date },
        },
        setup(props) {
            const store = useStore();

            const isCheckout = computed(() => props.placement === 'checkout');
            const isPayment = computed(() => props.placement === 'payment');
            const isBooking = computed(() => props.placement === 'booking');

            let product: ComputedRef<MainProduct | undefined>;
            let title: ComputedRef<string | undefined> = computed(() => undefined);

            if (isCheckout.value || isPayment.value) {
                product = computed<MainProduct | undefined>(() => {
                    const p: AnyProduct | undefined = store.getters['cart/getProduct'](
                        props.productId
                    );
                    return p && isMainProduct(p.Type) ? (p as MainProduct) : undefined;
                });

                const productCancellation = computed<CartProductCancellation | undefined>(
                    () => product.value && getCartProductCancellation(product.value)
                );

                title = computed<string | undefined>(() => {
                    if (!productCancellation.value || !productCancellation.value.Refundable) {
                        return undefined;
                    }

                    const policy = getFirstAmountCartPolicy(productCancellation.value);

                    const title =
                        policy && policy.EndDate
                            ? t({
                                  key: 'cancellation.free_cancellation_before',
                                  values: [
                                      getLocaleDayShortMonth(convertToDate(policy.EndDate)),
                                  ],
                              })
                            : undefined;

                    return title;
                });
            } else {
                const mainBookingProducts = computed(
                    () => store.getters['myBooking/getMainProducts']
                );
                product = mainBookingProducts.value[0];
            }

            const cancellationDescription = computed<string>(() => {
                const productType = product.value?.Type;
                const prefix = title.value ? ' - ' : '';

                switch (props.placement) {
                    case 'checkout':
                        return productType === ProductType.Hotel
                            ? t({
                                  key: 'cancellation.full_refund_accommodation',
                                  prefix,
                              })
                            : t({
                                  key: 'cancellation.full_refund_activity',
                                  prefix,
                              });
                    case 'booking':
                        return t({
                            key: 'cancellation.full_refund_date',
                            values: [getLocaleDayShortMonth(convertToDate(props.date))],
                        });

                    case 'payment':
                        return t({ key: 'cancellation.full_refund' });
                    default:
                        return '';
                }
            });

            return {
                isCheckout,
                isBooking,
                title,
                cancellationDescription,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'RefundableNotification.scss';
</style>
